import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import cx from 'classnames';
import PropTypes from 'prop-types';

import {
  isEmptyCollection,
  isUrlNamesMatch,
  SiteConfigManager,
  useApplicationState,
  useArchivedSportsbookState,
  useSportsbookState,
} from '@atlas/core';
import { COMPONENT_TYPES, SportLink, sportsbookRoutes } from '@atlas/routing';
import { Icon, Label, SportItemsSkeleton } from '@atlas/ui';

import useOnScreen from 'common/utils/useOnScreen';
import { useSelectionsContext } from 'src/components/SportsbookSelections/SelectionsContext';
import checkIsIframe from 'src/helpers/checkIsIframe';
import { useIsRtl } from 'src/hooks/useIsRtl';

import SelectionsSportsListItem from './SelectionsSportsListItem';

const INDICATOR_WIDTH = 20;
const SPORTS_ITEM_WIDTH = 78;
const INDICATOR_OFFSET = (SPORTS_ITEM_WIDTH - INDICATOR_WIDTH) / 2;

const favoritesItem = {
  id: sportsbookRoutes.favorites,
  urlName: sportsbookRoutes.favorites,
};
const selectionsItem = {
  id: sportsbookRoutes.selections,
  urlName: sportsbookRoutes.selections,
};

const SportItem = props => {
  const { active, children, className, sportUrlName } = props;
  const { updateArchivedSportsbookState } = useArchivedSportsbookState();

  const itemClassName = cx(className, { active });

  const handleSetName = useCallback(() => {
    updateArchivedSportsbookState({ selectedSportUrlName: sportUrlName });
  }, [sportUrlName, updateArchivedSportsbookState]);

  return (
    <div className={itemClassName} onClick={handleSetName}>
      {children}
    </div>
  );
};

const SportsList = ({ className, results = false }) => {
  const sportsbookState = results ? useArchivedSportsbookState : useSportsbookState;
  const { filter = {}, initialized, loading, selectedSportUrlName, sports } = sportsbookState();
  const { selections } = useSelectionsContext() || {};
  const { pathname } = useLocation();
  const isRtl = useIsRtl();
  const isIframe = checkIsIframe();
  const [activeItemIndex, setActiveItemIndex] = useState(-1);
  const isCouponsActive = SiteConfigManager.getConfig('config.config.sportsbook.isCouponsActive');
  const sportPrematchPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.SPORT_PREMATCH);
  const sportLivePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.SPORT_LIVE);

  const SportListClassName = cx('sports-list-container', className);

  const { timeHours: timeHoursFilter } = filter;

  const isPrematch = pathname.includes(sportPrematchPath);
  const isLive = pathname.includes(sportLivePath);
  const isFavorites = pathname.includes(sportsbookRoutes.favorites);
  const activeSport = React.useRef(null);
  const isVisible = useOnScreen(activeSport);
  const isVisibleRef = React.useRef(isVisible);

  const { authenticated } = useApplicationState();

  useEffect(() => {
    if (initialized && activeSport.current && !isVisibleRef.current) {
      activeSport?.current?.scrollIntoView({
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [initialized]);

  const sportsItems = useMemo(() => {
    const items = [];
    if (authenticated && !results) {
      items.push(favoritesItem);
    }

    if (!timeHoursFilter && isPrematch && isCouponsActive && !isEmptyCollection(selections)) {
      items.push(selectionsItem);
    }

    items.push(...sports);

    return items;
  }, [authenticated, results, timeHoursFilter, isPrematch, isCouponsActive, selections, sports]);

  const handleItemClick = useCallback((e, isActive) => {
    if (isActive) {
      e.preventDefault();
    }
  }, []);

  useEffect(() => {
    const activeItemIndex = sportsItems.findIndex(item =>
      isUrlNamesMatch(item.urlName, selectedSportUrlName),
    );

    setActiveItemIndex(activeItemIndex);
  }, [selectedSportUrlName, sportsItems]);

  if (!initialized && loading) {
    return (
      <div className={SportListClassName}>
        <div className="sports-list sport-type-skeleton">
          <SportItemsSkeleton isMobile />
        </div>
      </div>
    );
  }

  if (sports.length === 0) {
    return null;
  }

  const SportComponent = results ? SportItem : SportLink;

  return (
    <div className={SportListClassName}>
      <div className="sports-list">
        {sportsItems.map(sport => {
          const { id, liveCount, name, totalCount, urlName } = sport;
          const isActive = isUrlNamesMatch(urlName, selectedSportUrlName);

          if (urlName === favoritesItem.urlName) {
            return (
              <div className="sports-item-wrapper" key={id}>
                <SportLink
                  active={isFavorites}
                  className="sports-item"
                  sportUrlName={sportsbookRoutes.favorites}
                >
                  <div className="sports-item-icon">
                    <Icon className="icon-star" height={32} name="fav" width={32} />
                  </div>
                  <Label className="sports-item-name" message="favorites" />
                </SportLink>
              </div>
            );
          }

          if (urlName === selectionsItem.urlName) {
            return (
              <div className="sports-item-wrapper" key={id}>
                <SelectionsSportsListItem sportPrematchPath={sportPrematchPath} />
              </div>
            );
          }

          return (
            <div className="sports-item-wrapper" key={id} ref={isActive ? activeSport : null}>
              <SportComponent
                active={isActive}
                className="sports-item"
                dataTestId="sport"
                dataTestItemId={sport.id}
                onClick={e => handleItemClick(e, isActive)}
                sportUrlName={urlName}
              >
                <div className="sports-item-icon">
                  <Icon alt={name} height={32} name={`sportIcon-${id}`} type="sports" width={32} />
                  {!results && (
                    <div className="sports-item-counter">{isLive ? liveCount : totalCount}</div>
                  )}
                </div>
                <span className="sports-item-name">{name}</span>
              </SportComponent>
            </div>
          );
        })}

        {activeItemIndex !== -1 && (
          <span
            className="sports-item-indicator"
            style={{
              [isRtl && !isIframe ? 'right' : 'left']:
                `${activeItemIndex * SPORTS_ITEM_WIDTH + INDICATOR_OFFSET}px`,
            }}
          />
        )}
      </div>
    </div>
  );
};
SportsList.propTypes = { results: PropTypes.bool };

export default SportsList;
